/**
 * GraphRAG API Service
 *
 * Handles communication with the GraphRAG backend API for:
 * - Natural language queries
 * - Hybrid retrieval (vector + graph)
 * - Visualization data generation
 * - Conversation management
 */

import profile360Config from '../../../config/profile360Config';

// Environment URLs for GraphRAG API
const GRAPHRAG_ENVIRONMENTS = {
  dev: 'https://api-dev.trustx.cloud/graphrag',
  preprod: 'https://api-stg.trustx.cloud/graphrag',  // stg = preprod/staging
};

class GraphRAGService {
  constructor() {
    // Get the API Gateway base URL
    // The profile360 base URL is like: https://xxx.execute-api.region.amazonaws.com/profile360
    // We need to derive: https://xxx.execute-api.region.amazonaws.com/graphrag
    const profile360BaseUrl = profile360Config.get('api.endpoints.base');

    // Check for saved environment preference
    const savedEnv = localStorage.getItem('graphrag_environment') || 'dev';
    this.currentEnvironment = savedEnv;

    // Priority: 1. Explicit env var, 2. Environment-specific URL, 3. Derived from profile360
    if (process.env.REACT_APP_GRAPHRAG_API_URL) {
      // Environment variable takes highest priority (useful for debugging)
      this.baseUrl = process.env.REACT_APP_GRAPHRAG_API_URL;
      this.defaultBaseUrl = this.baseUrl;
      console.log('GraphRAG using env var URL:', this.baseUrl);
    } else if (GRAPHRAG_ENVIRONMENTS[savedEnv]) {
      // Use environment-specific URL
      this.baseUrl = GRAPHRAG_ENVIRONMENTS[savedEnv];
      this.defaultBaseUrl = profile360BaseUrl.replace(/\/profile360\/?$/, '/graphrag');
    } else {
      // Fallback: Derive from profile360 base URL
      this.defaultBaseUrl = profile360BaseUrl.replace(/\/profile360\/?$/, '/graphrag');
      this.baseUrl = this.defaultBaseUrl;
    }

    this.graphragEndpoint = ''; // No additional endpoint needed since baseUrl includes /graphrag
    this.authToken = null;
  }

  /**
   * Get available environments
   */
  getAvailableEnvironments() {
    return Object.keys(GRAPHRAG_ENVIRONMENTS);
  }

  /**
   * Get current environment
   */
  getCurrentEnvironment() {
    return this.currentEnvironment;
  }

  /**
   * Switch GraphRAG environment (dev/preprod)
   * @param {string} env - Environment name ('dev' or 'preprod')
   */
  setEnvironment(env) {
    if (GRAPHRAG_ENVIRONMENTS[env]) {
      this.currentEnvironment = env;
      this.baseUrl = GRAPHRAG_ENVIRONMENTS[env];
      localStorage.setItem('graphrag_environment', env);
      console.log(`GraphRAG environment switched to: ${env} (${this.baseUrl})`);
      return true;
    }
    console.warn(`Unknown GraphRAG environment: ${env}`);
    return false;
  }

  /**
   * Set authentication token
   */
  setAuthToken(token) {
    this.authToken = token;
  }

  /**
   * Get common headers for API requests
   * Tenant is resolved from JWT claims on the backend — no X-Tenant-Name header needed
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  /**
   * Make API request with error handling
   */
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseUrl}${this.graphragEndpoint}${endpoint}`;

    try {
      console.log('GraphRAG API request:', url, options);

      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getHeaders(),
          ...options.headers,
        },
      });

      // Get raw response text first
      const responseText = await response.text();
      console.log('GraphRAG API response status:', response.status);
      console.log('GraphRAG API response text (first 500 chars):', responseText.substring(0, 500));

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        console.error('Raw response:', responseText);
        return {
          success: false,
          error: `Invalid JSON response: ${responseText.substring(0, 100)}...`,
          data: null,
        };
      }

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || `HTTP ${response.status}`,
          data: null,
        };
      }

      return {
        success: true,
        data,
        error: null,
      };
    } catch (error) {
      console.error('GraphRAG API error:', error);
      return {
        success: false,
        error: error.message || 'Network error',
        data: null,
      };
    }
  }

  // ============================================
  // Chat / Query Methods
  // ============================================

  /**
   * Submit a natural language query to GraphRAG
   * @param {string} query - Natural language question
   * @param {string} sessionId - Optional session ID for conversation continuity
   * @param {object} options - Additional options (strategy, includeVisualization)
   */
  async submitQuery(query, sessionId = null, options = {}) {
    const body = {
      query,
      session_id: sessionId,
      strategy: options.strategy || 'adaptive',
      include_visualization: options.includeVisualization !== false,
      include_graph_data: options.includeGraphData !== false,
    };

    return this.makeRequest('/chat', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Submit a query via v2 endpoint (LLM-generated Cypher prototype)
   * Same response shape as submitQuery — can be swapped transparently.
   */
  async submitQueryV2(query, sessionId = null) {
    return this.makeRequest('/chat/v2', {
      method: 'POST',
      body: JSON.stringify({ query, session_id: sessionId }),
    });
  }

  /**
   * Get conversation history for a session
   * @param {string} sessionId - Session ID
   */
  async getConversationHistory(sessionId) {
    return this.makeRequest(`/chat/${sessionId}`);
  }

  /**
   * Get suggested follow-up questions
   * @param {string} query - Current query
   * @param {string} sessionId - Session ID
   */
  async getSuggestions(query, sessionId = null) {
    return this.makeRequest('/chat/suggestions', {
      method: 'POST',
      body: JSON.stringify({ query, session_id: sessionId }),
    });
  }

  /**
   * Get starter suggestions (pre-query, tenant-specific)
   */
  async getStarterSuggestions() {
    return this.makeRequest('/suggestions');
  }

  // ============================================
  // Graph Expansion Methods
  // ============================================

  /**
   * Expand a node's immediate neighbors for granular graph traversal.
   * Uses deterministic Cypher (no LLM) for instant (~100ms) expansion.
   * @param {string} nodeId - Node identifier (email, group name, app name, etc.)
   * @param {string} nodeType - Node type (User, Group, Application, Repository, JiraProject, Department, etc.)
   * @returns {object} { success, data: { graph_data: { nodes, links }, metadata } }
   */
  async expandNode(nodeId, nodeType) {
    return this.makeRequest('/graph/expand', {
      method: 'POST',
      body: JSON.stringify({ node_id: nodeId, node_type: nodeType }),
    });
  }

  // ============================================
  // Tool Methods
  // ============================================

  /**
   * List all available MCP tools
   */
  async listTools() {
    return this.makeRequest('/tools');
  }

  /**
   * Execute a specific MCP tool
   * @param {string} toolName - Name of the tool
   * @param {object} parameters - Tool parameters
   */
  async executeTool(toolName, parameters = {}) {
    // API expects parameters wrapped in a "parameters" object
    return this.makeRequest(`/tools/${toolName}`, {
      method: 'POST',
      body: JSON.stringify({ parameters }),
    });
  }

  // ============================================
  // Visualization Methods
  // ============================================

  /**
   * Get visualization data for a query result
   * @param {object} options - Visualization options
   */
  async getVisualization(options = {}) {
    return this.makeRequest('/visualize', {
      method: 'POST',
      body: JSON.stringify({
        query: options.query,
        layout_type: options.layoutType || 'force',
        output_format: options.outputFormat || 'd3',
        include_relationships: options.includeRelationships !== false,
        max_nodes: options.maxNodes || 100,
      }),
    });
  }

  /**
   * Get user-centric visualization
   * @param {string} userEmail - User email
   */
  async getUserVisualization(userEmail) {
    return this.makeRequest('/visualize/user', {
      method: 'POST',
      body: JSON.stringify({
        email: userEmail,
        include_groups: true,
        include_applications: true,
        include_reporting_chain: true,
      }),
    });
  }

  /**
   * Get risk visualization
   * @param {string} riskType - Type of risk (privilege_creep, dormant, excessive_access)
   */
  async getRiskVisualization(riskType) {
    return this.makeRequest('/visualize/risk', {
      method: 'POST',
      body: JSON.stringify({
        risk_type: riskType,
        layout_type: 'grid',
      }),
    });
  }

  // ============================================
  // Agent Methods
  // ============================================

  /**
   * List available agents
   */
  async listAgents() {
    return this.makeRequest('/agents');
  }

  /**
   * Get agent details
   * @param {string} agentName - Agent name
   */
  async getAgent(agentName) {
    return this.makeRequest(`/agents/${agentName}`);
  }

  // ============================================
  // User 360 Profile Methods
  // ============================================

  /**
   * Get complete user 360 profile with all access, risk, and security data
   * @param {string} userEmail - User's email address
   * @returns {object} Complete user profile with insights
   */
  async getUserProfile360(userEmail) {
    try {
      // Fetch user full graph data
      const graphResult = await this.executeTool('get_user_full_graph', { email: userEmail });
      
      // Fetch risk data via natural language query for comprehensive insights
      const riskQuery = `Get all risk indicators for user ${userEmail} including privilege creep, dormant status, and excessive access analysis`;
      const riskResult = await this.submitQuery(riskQuery, null, {
        strategy: 'graph_first',
        includeVisualization: false,
        includeGraphData: false
      });

      // Combine the results
      const profile = {
        success: true,
        user: null,
        groups: [],
        applications: [],
        manager: null,
        directReports: [],
        risks: {
          privilegeCreep: null,
          dormant: null,
          excessiveAccess: null,
          overallScore: 0,
          riskCounts: { high: 0, medium: 0, low: 0 }
        },
        lastSeen: null,
        status: 'Active'
      };

      // Parse graph data from get_user_full_graph response
      console.log('getUserProfile360 - graphResult:', graphResult);

      if (graphResult.success && graphResult.data) {
        const responseData = graphResult.data;
        console.log('getUserProfile360 - responseData:', responseData);

        // Check API-level success (HTTP 200 but tool may have failed)
        if (responseData.success === false) {
          console.warn('getUserProfile360 - tool execution failed:', responseData.error);
        }

        // API returns { success, tool, data: [ { u: {...}, g: {...}, creep: {...}, ... }, ... ] }
        // makeRequest wraps this, so graphResult.data contains the API response
        const dataArray = responseData.data || (Array.isArray(responseData) ? responseData : []);
        console.log('getUserProfile360 - dataArray length:', dataArray?.length);
        
        if (Array.isArray(dataArray) && dataArray.length > 0) {
          const firstRecord = dataArray[0];
          
          // Cypher returns AS user/groups/applications (not u/g/app)
          // Support both key names for robustness
          const userData = firstRecord.user || firstRecord.u;
          if (userData) {
            profile.user = userData;
            profile.lastSeen = userData.lastLoginDate || userData.lastSignIn || userData.last_signin || userData.syncedAt;
            profile.status = userData.accountEnabled === false ? 'disabled' : (userData.status || userData.accountStatus || 'Active');
          }

          // Groups returned as array in firstRecord.groups, or spread across records as record.g
          if (Array.isArray(firstRecord.groups) && firstRecord.groups.length > 0) {
            profile.groups = firstRecord.groups
              .filter(g => g && g.name)
              .map(g => ({
                name: g.name,
                displayName: g.name,
                description: g.groupType || '',
                securityEnabled: g.groupType === 'security',
                isAdmin: g.isAdmin || false
              }));
          } else {
            const groups = new Map();
            dataArray.forEach(record => {
              const g = record.g || record.group;
              if (g && g.name) {
                groups.set(g.name, {
                  name: g.name,
                  displayName: g.name,
                  description: g.groupType || '',
                  securityEnabled: g.groupType === 'security',
                  isAdmin: g.isAdmin || false
                });
              }
            });
            profile.groups = Array.from(groups.values());
          }

          // Applications returned as array in firstRecord.applications, or spread as record.app
          if (Array.isArray(firstRecord.applications) && firstRecord.applications.length > 0) {
            profile.applications = firstRecord.applications
              .filter(a => a && (a.name || a.appId))
              .map(a => ({
                name: a.name,
                displayName: a.name,
                description: a.appType || a.description || ''
              }));
          } else {
            const apps = new Map();
            dataArray.forEach(record => {
              const app = record.app || record.application;
              if (app) {
                const appKey = app.appId || app.name || app.displayName;
                if (appKey) {
                  apps.set(appKey, {
                    name: app.displayName || app.name,
                    displayName: app.displayName || app.name,
                    description: app.appType || app.description || ''
                  });
                }
              }
            });
            profile.applications = Array.from(apps.values());
          }
          
          // Extract privilege creep data from 'creep' field
          if (firstRecord.creep) {
            const creep = firstRecord.creep;
            // Use privilegedGroups from PrivilegeCreepInsight node (primary source),
            // fall back to deriving from group isAdmin flag
            const privilegedGroupNames = (creep.privilegedGroups && creep.privilegedGroups.length > 0)
              ? creep.privilegedGroups
              : profile.groups.filter(g => g.isAdmin).map(g => g.name);
            profile.risks.privilegeCreep = {
              score: creep.riskScore || 0,
              classification: creep.classification || 'unknown',
              reasoning: creep.reasoning || '',
              groupsToReview: creep.groupsToReview || creep.groups_to_review || [],
              appsToReview: creep.appsToReview || creep.apps_to_review || [],
              privilegedGroups: privilegedGroupNames,
              privilegedGroupCount: creep.privilegedGroupCount || privilegedGroupNames.length,
              recommendations: creep.recommendations || []
            };
            this._updateRiskCounts(creep.riskScore || 0, profile.risks.riskCounts);
          }
          
          // Check for dormant data
          if (firstRecord.dormant) {
            profile.risks.dormant = {
              daysInactive: firstRecord.dormant.daysInactive || firstRecord.dormant.days_inactive || 0,
              riskLevel: firstRecord.dormant.riskLevel || firstRecord.dormant.risk_level || 'unknown',
              recommendations: firstRecord.dormant.recommendations || []
            };
            const dormantScore = profile.risks.dormant.daysInactive > 90 ? 80 : profile.risks.dormant.daysInactive > 60 ? 60 : 40;
            this._updateRiskCounts(dormantScore, profile.risks.riskCounts);
          }
          
          // Check for excessive access data
          if (firstRecord.excessive) {
            profile.risks.excessiveAccess = {
              score: firstRecord.excessive.score || 0,
              zScore: firstRecord.excessive.zScore || firstRecord.excessive.z_score || 0,
              riskLevel: firstRecord.excessive.riskLevel || 'unknown',
              description: firstRecord.excessive.description || ''
            };
            this._updateRiskCounts(profile.risks.excessiveAccess.score, profile.risks.riskCounts);
          }
          
          // Extract manager if present
          if (firstRecord.manager) {
            profile.manager = {
              name: firstRecord.manager.name || firstRecord.manager.displayName,
              displayName: firstRecord.manager.displayName,
              email: firstRecord.manager.email,
              jobTitle: firstRecord.manager.jobTitle
            };
          }
        }
      }

      // Parse additional risk data from natural language response (supplements graph data)
      if (riskResult.success && riskResult.data) {
        const riskData = riskResult.data;
        
        // Extract from response structure (could be in different formats)
        const responseData = riskData.data || riskData.results || riskData;
        
        // Look for risk indicators in the response (only if not already set from graph)
        if (Array.isArray(responseData)) {
          responseData.forEach(item => {
            this._parseRiskItem(item, profile.risks);
          });
        } else if (typeof responseData === 'object') {
          this._parseRiskItem(responseData, profile.risks);
        }

        // Also check the answer text for structured data
        if (riskData.answer || riskData.response) {
          this._parseRiskFromText(riskData.answer || riskData.response, profile.risks);
        }
      }

      // Calculate overall risk score based on all collected data
      profile.risks.overallScore = this._calculateOverallRisk(profile.risks);
      
      console.log('User 360 profile loaded:', {
        user: profile.user?.email,
        groups: profile.groups?.length,
        risks: profile.risks
      });

      return profile;
    } catch (error) {
      console.error('Failed to fetch user 360 profile:', error);
      return {
        success: false,
        error: error.message,
        user: null,
        groups: [],
        applications: [],
        risks: {
          privilegeCreep: null,
          dormant: null,
          excessiveAccess: null,
          overallScore: 0,
          riskCounts: { high: 0, medium: 0, low: 0 }
        }
      };
    }
  }

  /**
   * Parse risk item from API response
   * @private
   */
  _parseRiskItem(item, risks) {
    if (!item) return;

    // Privilege creep detection - only if not already set
    if (!risks.privilegeCreep && (
        item.privilegeCreepScore !== undefined || item.privilege_creep_score !== undefined ||
        item.classification === 'significant_creep' || item.classification === 'moderate_creep' ||
        item.classification === 'HIGH_RISK_CREEP')) {
      const score = item.privilegeCreepScore || item.privilege_creep_score || item.risk_score || item.riskScore || 0;
      risks.privilegeCreep = {
        score: Math.round(score),
        classification: item.classification || this._getRiskLevel(score),
        reasoning: item.reasoning || item.description || 'Accumulated permissions exceed role requirements',
        groupsToReview: item.groupsToReview || item.groups_to_review || [],
        appsToReview: item.appsToReview || item.apps_to_review || []
      };
      this._updateRiskCounts(score, risks.riskCounts);
    }

    // Dormant user detection - only if not already set
    if (!risks.dormant && (
        item.daysSinceActivity !== undefined || item.days_inactive !== undefined ||
        item.daysInactive !== undefined || item.isDormant)) {
      const days = item.daysSinceActivity || item.days_inactive || item.daysInactive || 0;
      const score = days > 90 ? 80 : days > 60 ? 60 : days > 30 ? 40 : 20;
      risks.dormant = {
        daysInactive: days,
        riskLevel: item.riskLevel || item.risk_level || this._getRiskLevel(score),
        recommendations: item.recommendations || [`Review account activity`, `Consider access review`]
      };
      this._updateRiskCounts(score, risks.riskCounts);
    }

    // Excessive access detection - only if not already set
    if (!risks.excessiveAccess && (
        item.zScore !== undefined || item.z_score !== undefined ||
        item.excessiveAccessScore !== undefined || item.excessive_access_score !== undefined)) {
      const zScore = item.zScore || item.z_score || 0;
      const score = item.excessiveAccessScore || item.excessive_access_score || Math.round(Math.abs(zScore) * 25);
      risks.excessiveAccess = {
        score: Math.round(score),
        zScore: zScore,
        riskLevel: item.riskLevel || item.risk_level || this._getRiskLevel(score),
        description: item.description || `Access level ${zScore > 2 ? 'significantly ' : ''}exceeds peer average`
      };
      this._updateRiskCounts(score, risks.riskCounts);
    }
  }

  /**
   * Parse risk information from text response
   * @private
   */
  _parseRiskFromText(text, risks) {
    if (!text || typeof text !== 'string') return;

    const textLower = text.toLowerCase();

    // Look for privilege creep mentions
    if (textLower.includes('privilege creep') || textLower.includes('accumulated permissions')) {
      if (!risks.privilegeCreep) {
        const scoreMatch = text.match(/score[:\s]+(\d+)/i) || text.match(/(\d+)%?\s*risk/i);
        risks.privilegeCreep = {
          score: scoreMatch ? parseInt(scoreMatch[1]) : 50,
          classification: 'moderate_creep',
          reasoning: 'Detected from analysis'
        };
        this._updateRiskCounts(risks.privilegeCreep.score, risks.riskCounts);
      }
    }

    // Look for dormant mentions
    if (textLower.includes('dormant') || textLower.includes('inactive')) {
      if (!risks.dormant) {
        const daysMatch = text.match(/(\d+)\s*days?/i);
        const days = daysMatch ? parseInt(daysMatch[1]) : 60;
        risks.dormant = {
          daysInactive: days,
          riskLevel: days > 90 ? 'high' : days > 60 ? 'medium' : 'low'
        };
        this._updateRiskCounts(days > 90 ? 80 : days > 60 ? 60 : 40, risks.riskCounts);
      }
    }

    // Look for excessive access mentions
    if (textLower.includes('excessive access') || textLower.includes('exceeds peer')) {
      if (!risks.excessiveAccess) {
        const zMatch = text.match(/z-?score[:\s]+([\d.]+)/i);
        const zScore = zMatch ? parseFloat(zMatch[1]) : 2.5;
        risks.excessiveAccess = {
          score: Math.round(zScore * 25),
          zScore: zScore,
          riskLevel: zScore > 3 ? 'high' : zScore > 2 ? 'medium' : 'low'
        };
        this._updateRiskCounts(risks.excessiveAccess.score, risks.riskCounts);
      }
    }
  }

  /**
   * Get risk level from score
   * @private
   */
  _getRiskLevel(score) {
    if (score >= 80) return 'critical';
    if (score >= 60) return 'high';
    if (score >= 40) return 'medium';
    if (score >= 20) return 'low';
    return 'minimal';
  }

  /**
   * Update risk counts based on score
   * @private
   */
  _updateRiskCounts(score, counts) {
    if (score >= 60) counts.high++;
    else if (score >= 40) counts.medium++;
    else if (score > 0) counts.low++;
  }

  /**
   * Calculate overall risk score
   * @private
   */
  _calculateOverallRisk(risks) {
    const scores = [];
    if (risks.privilegeCreep?.score) scores.push(risks.privilegeCreep.score);
    if (risks.dormant?.daysInactive) {
      const days = risks.dormant.daysInactive;
      scores.push(days > 90 ? 80 : days > 60 ? 60 : days > 30 ? 40 : 20);
    }
    if (risks.excessiveAccess?.score) scores.push(risks.excessiveAccess.score);
    
    if (scores.length === 0) return 0;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }

  // ============================================
  // User Risk Insights (Detailed Analysis)
  // Uses Profile360 APIs (RDS) as source of truth
  // ============================================

  /**
   * Get comprehensive risk insights for a user (for detailed popup)
   * Calls Profile360 APIs (RDS source of truth) for all 9 risk categories
   * @param {string} userEmail - User's email address
   * @param {object} apiService - Profile360 apiService instance
   * @returns {object} Comprehensive risk insights
   */
  async getUserRiskInsights(userEmail, apiService) {
    try {
      console.log('getUserRiskInsights - fetching from Profile360 APIs for:', userEmail);

      // Initialize risk insights structure
      const insights = {
        riskScore: 0,
        totalInsights: 9,
        highRisk: 0,
        mediumRisk: 0,
        lowRisk: 0,
        risks: [],
        lastUpdated: new Date().toISOString()
      };

      // Fetch all data in parallel from Profile360 APIs (RDS)
      // Using email query params (not user_id paths) as that's what works
      // Note: velocity comes from trustDegradation, peer comparison from excessiveAccess
      const [
        dormantResult,
        privilegeCreepResult,
        excessiveAccessResult,
        suspiciousActivityResult,
        anomaliesResult,
        staleAccessResult,
        trustDegradationResult
      ] = await Promise.allSettled([
        // 1. Dormant Account - /api/analytics/user-insights
        apiService?.getUserInsights?.(userEmail).catch(() => null),
        // 2. Privilege Creep - /api/privilege-creep/users?email=
        apiService?.getPrivilegeCreepUsers?.({ email: userEmail }).catch(() => null),
        // 3. Excessive Access - /api/excessive-access/insights?email= (also provides peer comparison)
        apiService?.getExcessiveAccessInsights?.({ email: userEmail }).catch(() => null),
        // 4. Suspicious Activity - /api/suspicious-activity/users?email=
        apiService?.getSuspiciousActivityUsers?.({ email: userEmail }).catch(() => null),
        // 5. Behavioral Anomalies - /api/analytics/anomalies
        apiService?.detectAnomalies?.({ userIds: [userEmail] }).catch(() => null),
        // 6. Stale Access - /api/users/{email}/stale-apps
        apiService?.getStaleAppsForUser?.(userEmail).catch(() => null),
        // 7-9. Trust Degradation - /api/trust-degradation/scores?email= (also provides velocity)
        apiService?.getTrustDegradationScores?.({ email: userEmail }).catch(() => null)
      ]);

      // Helper to extract data from Promise.allSettled results
      // Handles both direct data and nested {data: ...} or {data: {insights: [...]}} structures
      const getData = (result) => {
        if (result.status !== 'fulfilled') return null;
        const val = result.value;
        // Handle nested structures: {data: {insights: [...]}} or {data: [...]} or direct
        if (val?.data?.insights) return val.data.insights;
        if (val?.data) return Array.isArray(val.data) ? val.data : val.data;
        return val;
      };

      // Helper to find user data in array response
      const findUserData = (data, email) => {
        if (!data) return null;
        if (Array.isArray(data)) {
          return data.find(d => 
            d.user_email?.toLowerCase() === email.toLowerCase() ||
            d.email?.toLowerCase() === email.toLowerCase()
          ) || data[0]; // Fallback to first if filtered by email already
        }
        return data;
      };

      // 1. Dormant Account
      const dormantData = getData(dormantResult);
      const userDormant = findUserData(dormantData, userEmail);
      const daysInactive = userDormant?.days_inactive || userDormant?.daysInactive || 0;
      const dormantLevel = daysInactive > 90 ? 'high' : daysInactive > 60 ? 'medium' : daysInactive > 30 ? 'medium' : 'low';
      insights.risks.push({
        label: 'Dormant Account',
        description: daysInactive > 0 ? `Account inactivity: ${daysInactive} days` : 'Account is active',
        level: dormantLevel,
        impact: dormantLevel,
        score: daysInactive > 90 ? 85 : daysInactive > 60 ? 65 : daysInactive > 30 ? 40 : 10,
        details: userDormant
      });

      // 2. Privilege Creep
      const privilegeData = getData(privilegeCreepResult);
      const userPrivilege = findUserData(privilegeData, userEmail);
      const privilegeScore = userPrivilege?.risk_score || userPrivilege?.riskScore || 0;
      const privilegeLevel = privilegeScore >= 70 ? 'high' : privilegeScore >= 40 ? 'medium' : 'low';
      insights.risks.push({
        label: 'Privilege Creep',
        description: userPrivilege?.reasoning || userPrivilege?.description || 
          (privilegeScore > 50 ? 'More roles than needed for job function' : 'No privilege creep detected'),
        level: privilegeLevel,
        impact: privilegeLevel,
        score: privilegeScore || 10,
        details: userPrivilege
      });

      // 3. Excessive Access
      const excessiveData = getData(excessiveAccessResult);
      const userExcessive = findUserData(excessiveData, userEmail);
      const zScore = userExcessive?.app_count_z_score || userExcessive?.z_score || userExcessive?.zScore || 0;
      const excessiveRiskScore = userExcessive?.risk_score || Math.min(Math.abs(zScore) * 25, 100);
      const excessiveLevel = excessiveRiskScore >= 70 ? 'high' : excessiveRiskScore >= 40 ? 'medium' : 'low';
      insights.risks.push({
        label: 'Excessive Access',
        description: userExcessive?.reasoning || userExcessive?.description || 
          (zScore > 2 ? `Access ${zScore.toFixed(1)}x above peer average` : 'Access levels within normal range'),
        level: excessiveLevel,
        impact: excessiveLevel,
        score: excessiveRiskScore || 10,
        zScore: zScore,
        classification: userExcessive?.classification,
        details: userExcessive
      });

      // 4. Suspicious Activity
      const suspiciousData = getData(suspiciousActivityResult);
      const userSuspicious = findUserData(suspiciousData, userEmail);
      const suspiciousScore = userSuspicious?.suspicion_score || userSuspicious?.risk_score || 0;
      const suspiciousLevel = suspiciousScore >= 70 ? 'high' : suspiciousScore >= 40 ? 'medium' : 'low';
      insights.risks.push({
        label: 'Suspicious Activity',
        description: userSuspicious?.reasoning || userSuspicious?.description || 
          (suspiciousScore > 50 ? 'Suspicious activity patterns detected' : 'No suspicious activity detected'),
        level: suspiciousLevel,
        impact: suspiciousLevel,
        score: suspiciousScore || 10,
        details: userSuspicious
      });

      // 5. Behavioral Anomalies
      const anomaliesData = getData(anomaliesResult);
      const userAnomalies = findUserData(anomaliesData, userEmail);
      const anomalyCount = userAnomalies?.total_anomalies || userAnomalies?.anomalies?.length || 0;
      const anomalyLevel = anomalyCount >= 3 ? 'high' : anomalyCount >= 1 ? 'medium' : 'low';
      insights.risks.push({
        label: 'Behavioral Anomalies',
        description: userAnomalies?.description || 
          (anomalyCount > 0 ? `${anomalyCount} behavioral anomaly(s) detected` : 'No anomalies detected'),
        level: anomalyLevel,
        impact: anomalyLevel,
        score: anomalyCount >= 3 ? 80 : anomalyCount >= 1 ? 50 : 10,
        details: userAnomalies
      });

      // 6. Stale Access
      const staleData = getData(staleAccessResult);
      const staleCount = Array.isArray(staleData) ? staleData.length : (staleData?.count || staleData?.stale_apps?.length || 0);
      const staleLevel = staleCount >= 5 ? 'high' : staleCount >= 2 ? 'medium' : 'low';
      insights.risks.push({
        label: 'Stale Access',
        description: staleCount > 0 ? `${staleCount} stale application(s) detected` : 'No stale access detected',
        level: staleLevel,
        impact: staleLevel,
        score: staleCount >= 5 ? 75 : staleCount >= 2 ? 45 : 10,
        details: staleData
      });

      // 7. Access Change Velocity (from Trust Degradation data)
      const trustData = getData(trustDegradationResult);
      const userTrust = findUserData(trustData, userEmail);
      const velocityData = userTrust?.velocity || {};
      const groupsChanged = (velocityData.groups_added_7d || 0) + (velocityData.groups_removed_7d || 0);
      const appsChanged = (velocityData.apps_added_7d || 0) + (velocityData.apps_removed_7d || 0);
      const totalChanges = groupsChanged + appsChanged;
      const velocityRiskLevel = velocityData.risk_level?.toLowerCase() || (totalChanges >= 10 ? 'high' : totalChanges >= 4 ? 'medium' : 'low');
      insights.risks.push({
        label: 'Access Change Velocity',
        description: totalChanges > 0 ? `${totalChanges} access changes in 7 days (${groupsChanged} groups, ${appsChanged} apps)` : 'Normal access change rate',
        level: velocityRiskLevel,
        impact: velocityRiskLevel,
        score: velocityRiskLevel === 'high' ? 80 : velocityRiskLevel === 'medium' ? 50 : 10,
        details: velocityData
      });

      // 8. Peer Access Comparison (from Excessive Access data)
      const peerPercentile = userExcessive?.group_count_percentile || 0;
      const peerLevel = peerPercentile >= 80 ? 'high' : peerPercentile >= 50 ? 'medium' : 'low';
      insights.risks.push({
        label: 'Peer Access Comparison',
        description: userExcessive?.peer_cohort_name 
          ? `${peerPercentile}th percentile in ${userExcessive.peer_cohort_name} (${userExcessive.peer_cohort_size} peers)`
          : (peerPercentile >= 50 ? `Above ${peerPercentile}% of peers` : 'Within peer group average'),
        level: peerLevel,
        impact: peerLevel,
        score: peerPercentile >= 80 ? 75 : peerPercentile >= 50 ? 45 : 10,
        details: { percentile: peerPercentile, cohort: userExcessive?.peer_cohort_name }
      });

      // 9. Compliance Risk (Trust Degradation Score)
      const trustScore = userTrust?.trust_score || 100;
      const trustLevel = userTrust?.trust_level || 'GOOD';
      // Invert trust score to risk score (low trust = high risk)
      const complianceRiskScore = Math.max(0, 100 - trustScore);
      const complianceLevel = trustLevel === 'CRITICAL' || trustLevel === 'DEGRADED' ? 'high' 
        : trustLevel === 'FAIR' ? 'medium' : 'low';
      const trendDirection = userTrust?.trend?.direction;
      insights.risks.push({
        label: 'Compliance Risk',
        description: userTrust 
          ? `Trust level: ${trustLevel} (score: ${trustScore})${trendDirection ? `, trend: ${trendDirection}` : ''}`
          : 'No compliance data available',
        level: complianceLevel,
        impact: complianceLevel,
        score: complianceRiskScore || 10,
        trustScore: trustScore,
        trustLevel: trustLevel,
        details: userTrust
      });

      // Calculate aggregated stats
      insights.risks.forEach(risk => {
        if (risk.level === 'high') insights.highRisk++;
        else if (risk.level === 'medium') insights.mediumRisk++;
        else insights.lowRisk++;
      });

      // Calculate overall risk score (weighted average)
      const totalScore = insights.risks.reduce((sum, r) => sum + (r.score || 0), 0);
      insights.riskScore = Math.round(totalScore / insights.risks.length);

      console.log('getUserRiskInsights - result from Profile360 APIs:', insights);
      return { success: true, data: insights };

    } catch (error) {
      console.error('getUserRiskInsights error:', error);
      return { 
        success: false, 
        error: error.message,
        data: this._getDefaultRiskInsights()
      };
    }
  }

  /**
   * Get default/empty risk insights structure
   * @private
   */
  _getDefaultRiskInsights() {
    return {
      riskScore: 0,
      totalInsights: 9,
      highRisk: 0,
      mediumRisk: 0,
      lowRisk: 9,
      risks: [
        { label: 'Dormant Account', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Privilege Creep', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Excessive Access', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Suspicious Activity', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Behavioral Anomalies', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Stale Access', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Access Change Velocity', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Peer Access Comparison', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 },
        { label: 'Compliance Risk', description: 'Unable to fetch data', level: 'low', impact: 'low', score: 0 }
      ],
      lastUpdated: new Date().toISOString()
    };
  }

  // ============================================
  // Access Review Methods
  // ============================================

  /**
   * Start an access review session
   * @param {string} scope - Scope type: 'all', 'department', or 'risk_type'
   * @param {string} scopeValue - Scope value (department name or risk type)
   */
  async startReview(scope, scopeValue = null, sessionId = null) {
    return this.makeRequest('/review/start', {
      method: 'POST',
      body: JSON.stringify({ scope, scope_value: scopeValue, session_id: sessionId }),
    });
  }

  /**
   * Get detailed user review card
   * @param {string} reviewId - Review session ID
   * @param {string} email - User email
   */
  async getReviewUserDetail(reviewId, email) {
    return this.makeRequest(`/review/${reviewId}/user/${encodeURIComponent(email)}`);
  }

  /**
   * Submit a review decision for a user
   * @param {string} reviewId - Review session ID
   * @param {string} userEmail - User email
   * @param {string} decision - Decision: 'approve', 'flag', or 'revoke'
   * @param {string} comment - Optional comment
   */
  async submitReviewDecision(reviewId, userEmail, decision, comment = null) {
    return this.makeRequest('/review/decision', {
      method: 'POST',
      body: JSON.stringify({
        review_id: reviewId,
        user_email: userEmail,
        decision,
        comment,
      }),
    });
  }

  /**
   * Send a chat message in review context
   * @param {string} reviewId - Review session ID
   * @param {string} message - Chat message
   * @param {string} sessionId - Session ID
   * @param {string} userEmail - Optional user email for context
   */
  async reviewChat(reviewId, message, sessionId = null, userEmail = null) {
    return this.makeRequest('/review/chat', {
      method: 'POST',
      body: JSON.stringify({
        review_id: reviewId,
        message,
        session_id: sessionId,
        user_email: userEmail,
      }),
    });
  }

  /**
   * List past review sessions for the current reviewer
   * @returns {object} { sessions: [...] }
   */
  async getReviewSessions() {
    return this.makeRequest('/review/sessions');
  }

  /**
   * Get full message history for a past review session
   * @param {string} sessionId - Session ID
   * @returns {object} { session: {...}, messages: [...] }
   */
  async getReviewSessionMessages(sessionId) {
    return this.makeRequest(`/review/sessions/${encodeURIComponent(sessionId)}`);
  }

  // ============================================
  // Demo Risk Scenarios Chat
  // ============================================

  /**
   * Send a chat message for risk scenario analysis
   * @param {string} query - User's question
   * @param {Array} conversationHistory - Previous messages [{role, content}]
   */
  async demoRiskChat(query, conversationHistory = []) {
    return this.makeRequest('/demo/risk-chat', {
      method: 'POST',
      body: JSON.stringify({
        query,
        conversation_history: conversationHistory,
      }),
    });
  }

  /**
   * Get starter suggestions for risk scenario chat
   */
  async getDemoRiskChatSuggestions() {
    return this.makeRequest('/demo/risk-chat/suggestions');
  }

  // ============================================
  // Utility Methods
  // ============================================

  /**
   * Health check
   */
  async healthCheck() {
    return this.makeRequest('/health');
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
const graphragService = new GraphRAGService();
export default graphragService;
